import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BrandMark } from "@/components/BrandMark";
import {
  ADMIN_ROUTE,
  MAGIC_CLICK_COUNT,
  MAGIC_CLICK_WINDOW_MS,
} from "@/lib/constants";

// Premium "master OFF" gate. Displays the exact required headline:
//   "Coming Soon - July 10, 2026"
// Used by the App-level Shell when the backend's `site_live` flag is false
// AND the visitor is NOT on the admin route. Self-contained — does not
// touch any existing page, layout, or storage helper.
const LAUNCH_DATE = new Date("2026-07-10T00:00:00+05:30");

const formatCell = (n) => String(Math.max(0, Math.floor(n))).padStart(2, "0");

const useCountdown = (target) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = target.getTime() - now;
  if (diff <= 0) {
    return { days: 0, hours: 0, mins: 0, secs: 0, done: true };
  }
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const mins = Math.floor((diff / (1000 * 60)) % 60);
  const secs = Math.floor((diff / 1000) % 60);
  return { days, hours, mins, secs, done: false };
};

export const ComingSoonScreen = () => {
  const c = useCountdown(LAUNCH_DATE);
  const navigate = useNavigate();

  // Magic 5-tap backdoor on the logo — mirrors Header.jsx so the admin can
  // still reach /admin even while the master switch is OFF. Works on mobile
  // (touch-action: manipulation removes the 300ms tap delay) and desktop.
  const clicks = useRef([]);
  const handleLogoTap = () => {
    const now = Date.now();
    clicks.current = clicks.current.filter(
      (t) => now - t <= MAGIC_CLICK_WINDOW_MS,
    );
    clicks.current.push(now);
    if (clicks.current.length >= MAGIC_CLICK_COUNT) {
      clicks.current = [];
      navigate(ADMIN_ROUTE);
    }
  };

  return (
    <div
      data-testid="master-off-screen"
      className="fixed inset-0 z-[60] flex items-center justify-center px-6 py-10 overflow-hidden"
    >
      {/* Subtle layered backdrop on top of the existing mesh */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#FAF7F1]/40 via-white/55 to-[#FFF5D1]/55 backdrop-blur-[6px]" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 18% 26%, #022C22 1px, transparent 1.4px), radial-gradient(circle at 82% 74%, #E0B53C 1px, transparent 1.4px)",
          backgroundSize: "32px 32px, 44px 44px",
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
        className="relative w-full max-w-2xl text-center"
      >
        <div className="flex justify-center mb-7">
          <button
            type="button"
            onClick={handleLogoTap}
            data-testid="coming-soon-magic-logo"
            aria-label="MastiBazaar"
            className="bg-transparent border-0 p-2 -m-2 cursor-default select-none focus:outline-none"
            style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
          >
            <BrandMark size="sm" />
          </button>
        </div>

        <p
          className="text-[11px] uppercase tracking-[0.42em] text-[#064E3B] font-semibold mb-4"
          data-testid="master-off-eyebrow"
        >
          MastiBazaar
        </p>

        {/* EXACT required headline */}
        <h1
          data-testid="master-off-headline"
          className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#0B0C10] font-light tracking-tight leading-[1.02]"
        >
          Coming Soon - July 10, 2026
        </h1>

        <p className="mt-6 text-sm sm:text-base text-neutral-600 max-w-md mx-auto leading-relaxed">
          Something premium is on its way. We're putting the finishing touches
          on the bazaar — see you on launch day.
        </p>

        {/* Countdown */}
        <div
          data-testid="master-off-countdown"
          className="mt-10 grid grid-cols-4 gap-3 sm:gap-4 max-w-md mx-auto"
        >
          {[
            { label: "Days", value: c.days },
            { label: "Hours", value: c.hours },
            { label: "Mins", value: c.mins },
            { label: "Secs", value: c.secs },
          ].map((cell) => (
            <div
              key={cell.label}
              className="rounded-2xl border border-white/70 bg-white/65 backdrop-blur-xl py-3 sm:py-4 shadow-[0_8px_24px_rgba(2,44,34,0.07)]"
            >
              <p className="font-display text-2xl sm:text-3xl text-[#022C22] font-light tabular-nums">
                {formatCell(cell.value)}
              </p>
              <p className="text-[9px] uppercase tracking-[0.24em] text-neutral-500 mt-1 font-medium">
                {cell.label}
              </p>
            </div>
          ))}
        </div>

        {/* Download APK button — appears under the countdown. Points to
            /mastibazaar.apk (upload the APK file to /app/frontend/public/ or
            configure via CDN); browser's `download` attr triggers a native
            download instead of navigation. Uses the existing dark-emerald +
            gold theme for perfect visual continuity. */}
<a
      href="https://google.com"



 
  
 
        
  

         

         

          download="MastiBazaar.apk"
          data-testid="master-off-download-apk"
          className="mt-8 inline-flex items-center gap-2 bg-[#022C22] text-[#E0B53C] rounded-full px-6 py-3 text-sm font-semibold shadow-[0_10px_28px_rgba(2,44,34,0.25)] hover:-translate-y-0.5 hover:shadow-[0_14px_32px_rgba(2,44,34,0.32)] transition-all"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download APK
        </a>

        <p className="mt-12 text-[10px] uppercase tracking-[0.32em] text-neutral-400 font-medium">
          mastibazaar.in · Crafted in India
        </p>
      </motion.div>
    </div>
  );
};
